/**
 * CR-7714 FE-7 / FE-20: judge and colour a KPI only against a real target,
 * and only when the KPI has a value.
 *
 * The group and site KPI tabs each carried their own fallback targets (PPM
 * 2000 / 1500 / 500, OTIF 95 / 88, OTD 90), different numbers on the same
 * page, and turned a missing OTIF/OTD/PPM into 0 before colouring it. A
 * supplier with no deliveries then showed a red 0% OTD beside a green PPM 0.
 * Here, no value or no target means no judgement: a neutral grey.
 */

/** Neutral colour for "nothing to judge" (no value, or no target configured). */
export const NO_JUDGEMENT_COLOR = '#9ca3af';

/**
 * The first candidate that is a real number.
 *
 * @param candidates - Target sources in priority order (e.g. settings, then payload)
 * @returns The target, or null when none is configured, never an invented default
 */
export function firstTarget(...candidates: Array<number | null | undefined>): number | null {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
  }
  return null;
}

/**
 * Three-tier colour of a value against its target.
 *
 * @param value - The KPI (null/undefined = no data)
 * @param target - The configured target (null = none)
 * @param inverse - true when lower is better (PPM)
 * @returns green / orange / red, or {@link NO_JUDGEMENT_COLOR} when either side is missing
 */
export function tierColor(
  value: number | null | undefined,
  target: number | null | undefined,
  inverse = false,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NO_JUDGEMENT_COLOR;
  if (target === null || target === undefined) return NO_JUDGEMENT_COLOR;
  if (inverse) {
    if (value <= target) return '#10b981';
    if (value <= target * 1.5) return '#ff9800';
    return '#ef4444';
  }
  if (value >= target) return '#10b981';
  if (value >= target * 0.8) return '#ff9800';
  return '#ef4444';
}

/**
 * Tailwind classes for the PPM badge: red over target, green within it,
 * muted when there is no PPM or no target to compare with.
 *
 * @param value - PPM (null = no data)
 * @param target - PPM target (null = none)
 * @returns Badge class names
 */
export function ppmBadgeClass(value: number | null | undefined, target: number | null | undefined): string {
  if (value === null || value === undefined || target === null || target === undefined) {
    return 'bg-muted text-muted-foreground';
  }
  return value > target ? 'bg-destructive text-destructive-foreground' : 'bg-green-600 text-white';
}

/** One metric in a row verdict. */
export interface RowMetric {
  value: number | null;
  target: number | null;
  /** false for PPM (lower is better). */
  higherIsBetter: boolean;
  /** Metrics hidden by the delivery-metric mode take no part. */
  shown: boolean;
}

/** Good = every shown metric has data and meets target; attention = one misses; na = not enough data. */
export type RowVerdict = 'good' | 'attention' | 'na';

/**
 * Judge a supplier row on the metrics it has.
 *
 * A metric with no value (or no target) is neither good nor bad: it cannot
 * make the row "Attention", and a row cannot be "Good" while one is unknown.
 *
 * @param metrics - The row's metrics
 * @returns The verdict
 */
export function judgeRow(metrics: RowMetric[]): RowVerdict {
  const shown = metrics.filter((m) => m.shown);
  let unknown = false;
  for (const m of shown) {
    if (m.value === null || m.target === null) { unknown = true; continue; }
    const ok = m.higherIsBetter ? m.value >= m.target : m.value <= m.target;
    if (!ok) return 'attention';
  }
  return unknown || shown.length === 0 ? 'na' : 'good';
}

/** Text colour for a table cell: judged only when the value and the target exist. */
export function judgeClass(value: number | null, target: number | null, higherIsBetter: boolean): string {
  if (value === null || target === null) return 'text-muted-foreground';
  return (higherIsBetter ? value >= target : value <= target) ? 'text-green-600' : 'text-red-600';
}
